/**
 * The single fetch wrapper every hook calls. Reads the API base URL from the
 * config singleton, attaches the Clerk session JWT as a Bearer token (the
 * backend's ClerkAuthentication resolves it to a MinimaUser), JSON-encodes
 * the body, and throws a typed APIError on a non-2xx response. The Django
 * CSRF token still rides along on mutations so session-cookie auth (basement
 * admin, tests) keeps working. A 401 means the session is gone — the store's
 * auth state is cleared so ProtectedRoute bounces to sign-in.
 */
import { getConfig } from "./config";
import { getCsrfToken } from "./csrf";
import { getClerkToken } from "@/services/clerk";
import { useStore } from "@/store";

export class APIError extends Error {
  status: number;
  data: any;

  constructor(message: string, status: number, data?: any) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

export const apiRequest = async (
  endpoint: string,
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH" = "GET",
  body: any = null,
  headers: Record<string, string> = {}
) => {
  const { apiUrl } = getConfig();

  const requestHeaders: Record<string, string> = {
    "Content-Type": "application/json",
    ...headers,
  };

  const clerkToken = await getClerkToken();
  if (clerkToken) {
    requestHeaders["Authorization"] = `Bearer ${clerkToken}`;
  }

  const options: RequestInit = {
    method,
    headers: requestHeaders,
    credentials: "include",
  };

  if (method !== "GET") {
    const csrf = getCsrfToken();
    if (csrf) {
      requestHeaders["X-CSRFToken"] = csrf;
    }
    options.body = JSON.stringify(body ?? {});
  }

  const response = await fetch(`${apiUrl}${endpoint}`, options);

  if (!response.ok) {
    // Session expired or revoked — clear auth so protected routes redirect.
    if (response.status === 401 && useStore.getState().user) {
      useStore.getState().clearAuth();
    }

    let errorData;
    try {
      errorData = await response.json();
    } catch {
      throw new APIError("Request failed", response.status);
    }

    throw new APIError(
      errorData.error || errorData.detail || "Request failed",
      response.status,
      errorData
    );
  }

  return response.json();
};

export default apiRequest;
